/* eslint-disable @typescript-eslint/no-explicit-any */
import { Autocomplete, Paper, TextField } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';

import { useModal } from 'shared/hooks';
import { Tooltip, useTooltpsForAutocomplete } from 'shared/hooks/use-tooltips-for';

import filterSvg from '../../../assets/img/filter.svg';
import { tabs } from '../../../constants/checlistTableTabs';
import { classArray } from '../../../constants/classArray';
import { Option } from '../../../shared/types';
import { PersonOption } from '../../../shared/types/works';

import './style.scss';

interface Props {
  subject?: Option | null;
  studentClass?: string | null;
  teacher?: PersonOption | null;
  student?: PersonOption | null;
  setSubjectFilter: (subject: Option) => void;
  setStudentFilter: (student: PersonOption) => void;
  setTeacherFilter?: (teacher: PersonOption) => void;
  setClassFilter: (classFilter: string) => void;
  students: any[];
  teachers?: any[];
  subjects: Option[];
  handleApply: () => void;
  handleReset: () => void;
  click: boolean;
  tab: string;
}

export default function ExamFilters({
  subject,
  student,
  teacher,
  studentClass,
  setSubjectFilter,
  setTeacherFilter,
  setStudentFilter,
  setClassFilter,
  teachers,
  students,
  subjects,
  handleApply,
  handleReset,
  tab,
}: Props) {
  const {
    t,
    i18n: { language: platformLang },
  } = useTranslation();
  const { open, close, isOpen } = useModal();
  const { getProps: getPropsForAutoComplete } = useTooltpsForAutocomplete();

  return (
    <>
      <div className="filter-wrap mb-[32px] hidden items-center justify-between gap-[12px] rounded-[8px] bg-white p-[24px] [box-shadow:6px_6px_0px_0px_rgba(156,_223,_252,_0.50)] lg:flex">
        <Autocomplete
          {...getPropsForAutoComplete('subject')}
          options={subjects.sort((a, b) => a.name.localeCompare(b.name)) as Option[]}
          value={subject}
          className="rounded-[32px] border-[1px] border-[#D1CEC7] lg:w-[280px]"
          onChange={(event, value) => setSubjectFilter(value as Option)}
          getOptionLabel={option => (platformLang === 'ua' ? option.name : option.ruName)}
          PaperComponent={({ children }) => (
            <Paper
              style={{
                borderWidth: '2px',
                borderColor: '#52C7FA',
                marginTop: '6px',
                borderRadius: '20px',
              }}
            >
              {children}
            </Paper>
          )}
          renderInput={params => (
            <TextField
              className="border-0 focus:outline-none"
              {...params}
              placeholder={t('Choose a subject')}
              value={subject?.[platformLang === 'ua' ? 'name' : 'ruName']}
            />
          )}
          sx={{
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderRadius: 30,
              borderColor: 'transparent',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderRadius: 30,
              borderColor: 'transparent',
            },
          }}
        />

        <Autocomplete
          {...getPropsForAutoComplete('studentClass')}
          autoComplete={false}
          className="rounded-[32px] border-[1px] border-[#D1CEC7] lg:w-[280px]"
          options={classArray}
          getOptionLabel={option => t(option)}
          value={studentClass}
          onChange={(e, value) => setClassFilter(value as string)}
          PaperComponent={({ children }) => (
            <Paper
              style={{
                borderWidth: '2px',
                borderColor: '#52C7FA',
                marginTop: '6px',
                borderRadius: '20px',
              }}
            >
              {children}
            </Paper>
          )}
          renderInput={params => (
            <TextField
              {...params}
              className="border-none focus:outline-none"
              value={studentClass ? t(studentClass) : ''}
              onChange={e => setClassFilter(e.target.value)}
              placeholder={t('Choose a class')}
            />
          )}
          sx={{
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderRadius: 30,
              borderColor: 'transparent',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderRadius: 30,
              borderColor: 'transparent',
            },
          }}
        />

        {(tab === tabs.all || tab === tabs.studentsWorks || tab === tabs.archived) && (
          <Autocomplete
            {...getPropsForAutoComplete('student')}
            autoComplete={false}
            className="rounded-[32px] border-[1px] border-[#D1CEC7] lg:w-[280px]"
            options={students
              .map(student => ({
                FIO: student.FIO,
                email: student.email ? student.email?.replace(/\./g, '') : '',
              }))
              .sort((a, b) => a.FIO.localeCompare(b.FIO))}
            value={student}
            isOptionEqualToValue={(option, value) => option.email === value.email}
            getOptionLabel={option => `${option.FIO}`}
            onChange={(e, value) => setStudentFilter(value as PersonOption)}
            PaperComponent={({ children }) => (
              <Paper
                style={{
                  borderWidth: '2px',
                  borderColor: '#52C7FA',
                  marginTop: '6px',
                  borderRadius: '20px',
                }}
              >
                {children}
              </Paper>
            )}
            renderInput={params => (
              <TextField
                {...params}
                className="border-none focus:outline-none"
                value={student}
                onChange={e => setStudentFilter({ FIO: e.target.value, email: '' })}
                placeholder={t('Chose a student')}
              />
            )}
            sx={{
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderRadius: 30,
                borderColor: 'transparent',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderRadius: 30,
                borderColor: 'transparent',
              },
            }}
          />
        )}

        {tab === tabs.all && teachers && setTeacherFilter && (
          <Autocomplete
            {...getPropsForAutoComplete('teacher')}
            autoComplete={false}
            options={teachers
              .map(teacher => {
                if (teacher.email) {
                  return {
                    FIO: teacher.FIO,
                    email: teacher.email?.replace(/\./g, ''),
                  };
                }
              })
              .sort((a, b) => a?.FIO.localeCompare(b?.FIO))}
            value={teacher}
            className="rounded-[32px] border-[1px] border-[#D1CEC7] lg:w-[280px]"
            isOptionEqualToValue={(option, value) => option?.email === value?.email}
            getOptionLabel={option => {
              return option ? `${option.FIO}` : '';
            }}
            onChange={(e, value) => setTeacherFilter(value as PersonOption)}
            PaperComponent={({ children }) => (
              <Paper
                style={{
                  borderWidth: '2px',
                  borderColor: '#52C7FA',
                  marginTop: '6px',
                  borderRadius: '20px',
                }}
              >
                {children}
              </Paper>
            )}
            renderInput={params => (
              <TextField
                {...params}
                className="border-none focus:outline-none"
                value={teacher}
                onChange={e => setTeacherFilter({ FIO: e.target.value, email: '' })}
                placeholder={t('Choose a teacher')}
              />
            )}
            sx={{
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderRadius: 30,
                borderColor: 'transparent',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderRadius: 30,
                borderColor: 'transparent',
              },
            }}
          />
        )}

        <div className="flex gap-[12px]">
          <Tooltip title="Reset">
            <button
              className="btn more small h-[40px] w-[40px] rounded-[8px] !p-[6px]"
              onClick={handleReset}
              disabled={!subject && !student && !teacher && !studentClass}
            >
              <img className="h-[24px] w-[24px]" src={filterSvg} alt={'filter'} />
            </button>
          </Tooltip>

          <button onClick={handleApply} className="btn btn-primary more">
            {t('Apply')}
          </button>
        </div>
      </div>

      <div className="lg:hidden">
        <button onClick={open} className="btn more">
          {t('Filter')}
        </button>

        <Modal
          isOpen={isOpen}
          onRequestClose={close}
          className="bottom-auto mx-auto w-[85%] translate-y-[25vh] rounded-xl bg-[#FAF8F4] px-[16px] py-[24px] lg:w-[520px]"
          overlayClassName="z-10 w-full fixed top-0 left-0 rigth-0 bottom-0 bg-[#0d0d0ccc]"
          closeTimeoutMS={300}
        >
          <div className="filter-wrap flex-col items-center justify-between">
            <Autocomplete
              {...getPropsForAutoComplete('subjectModal')}
              options={subjects as Option[]}
              value={subject}
              className="mb-[8px] rounded-[32px] border-[1px] border-[#D1CEC7] bg-white"
              onChange={(event, value) => setSubjectFilter(value as Option)}
              getOptionLabel={option => (platformLang === 'ua' ? option.name : option.ruName)}
              PaperComponent={({ children }) => (
                <Paper
                  style={{
                    borderWidth: '2px',
                    borderColor: '#52C7FA',
                    marginTop: '6px',
                    borderRadius: '20px',
                  }}
                >
                  {children}
                </Paper>
              )}
              renderInput={params => (
                <TextField
                  className="border-0 focus:outline-none"
                  {...params}
                  placeholder={t('Choose a subject')}
                  value={subject?.[platformLang === 'ua' ? 'name' : 'ruName']}
                />
              )}
              sx={{
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderRadius: 30,
                  borderColor: 'transparent',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderRadius: 30,
                  borderColor: 'transparent',
                },
              }}
            />

            <Autocomplete
              {...getPropsForAutoComplete('studentClassModal')}
              autoComplete={false}
              className="mb-[8px] rounded-[32px] border-[1px] border-[#D1CEC7] bg-white"
              options={classArray.map(it => t(it))}
              value={studentClass}
              onChange={(e, value) => setClassFilter(value as string)}
              PaperComponent={({ children }) => (
                <Paper
                  style={{
                    borderWidth: '2px',
                    borderColor: '#52C7FA',
                    marginTop: '6px',
                    borderRadius: '20px',
                  }}
                >
                  {children}
                </Paper>
              )}
              renderInput={params => (
                <TextField
                  {...params}
                  className="border-none focus:outline-none"
                  value={studentClass}
                  onChange={e => setClassFilter(e.target.value)}
                  placeholder={t('Choose a class')}
                />
              )}
              sx={{
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderRadius: 30,
                  borderColor: 'transparent',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderRadius: 30,
                  borderColor: 'transparent',
                },
              }}
            />

            {tab === tabs.all && (
              <Autocomplete
                {...getPropsForAutoComplete('studentModal')}
                autoComplete={false}
                className="mb-[8px] rounded-[32px] border-[1px] border-[#D1CEC7] bg-white"
                options={students.map(student => ({
                  FIO: student.FIO,
                  email: student.email ? student.email.replace('.com', 'com') : '',
                }))}
                value={student}
                isOptionEqualToValue={(option, value) => option.email === value.email}
                getOptionLabel={option => `${option.FIO}`}
                onChange={(e, value) => setStudentFilter(value as PersonOption)}
                PaperComponent={({ children }) => (
                  <Paper
                    style={{
                      borderWidth: '2px',
                      borderColor: '#52C7FA',
                      marginTop: '6px',
                      borderRadius: '20px',
                    }}
                  >
                    {children}
                  </Paper>
                )}
                renderInput={params => (
                  <TextField
                    {...params}
                    className="border-none focus:outline-none"
                    value={student}
                    onChange={e => setStudentFilter(e.target.value)}
                    placeholder={t('Chose a student')}
                  />
                )}
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderRadius: 30,
                    borderColor: 'transparent',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderRadius: 30,
                    borderColor: 'transparent',
                  },
                }}
              />
            )}

            {tab === tabs.all && teachers && setTeacherFilter && (
              <Autocomplete
                {...getPropsForAutoComplete('teacherModal')}
                autoComplete={false}
                options={teachers
                  .map(teacher => {
                    if (teacher.email) {
                      return {
                        FIO: teacher.FIO,
                        email: teacher.email?.replace(/\./g, ''),
                      };
                    }
                  })
                  .sort((a, b) => a.FIO.localeCompare(b.FIO))}
                value={teacher}
                className="mb-[8px] rounded-[32px] border-[1px] border-[#D1CEC7] bg-white"
                isOptionEqualToValue={(option, value) => option?.email === value?.email}
                getOptionLabel={option => `${option?.FIO ?? ''}`}
                onChange={(e, value) => setTeacherFilter(value as PersonOption)}
                PaperComponent={({ children }) => (
                  <Paper
                    style={{
                      borderWidth: '2px',
                      borderColor: '#52C7FA',
                      marginTop: '6px',
                      borderRadius: '20px',
                    }}
                  >
                    {children}
                  </Paper>
                )}
                renderInput={params => (
                  <TextField
                    {...params}
                    className="border-none focus:outline-none"
                    value={teacher}
                    onChange={e => setTeacherFilter(e.target.value)}
                    placeholder={t('Choose a teacher')}
                  />
                )}
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderRadius: 30,
                    borderColor: 'transparent',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderRadius: 30,
                    borderColor: 'transparent',
                  },
                }}
              />
            )}

            <div className="mt-[20px] w-full flex-col">
              <button
                title={t('Reset')}
                className="btn more mb-[12px] w-full"
                onClick={() => {
                  close();
                  handleReset();
                }}
                disabled={!subject && !student && !teacher && !studentClass}
              >
                <img className="h-[24px] w-[24px]" src={filterSvg} alt={'filter'} />

                {t('clearFilter')}
              </button>

              <button
                onClick={() => {
                  close();
                  handleApply();
                }}
                className="btn btn-primary more w-full"
              >
                {t('Apply')}
              </button>
            </div>
          </div>
        </Modal>
      </div>
    </>
  );
}
